import { TrainingConfig } from './trainingConfig.types';

export const trainingConfig: TrainingConfig = {
	id: 'bt-illuminate-onboarding',
	title: 'Illuminate Training',
	subtitle: 'Behavior Technician Onboarding',
	description:
		'Learn how to document your sessions in Illuminate — from logging in to submitting your SOAP note. Complete all eight modules, pass the quiz, and earn your training certificate.',
	passThreshold: 10,
	certificate: {
		orgName: 'The Lighthouse',
		heading: 'Certificate of Completion',
		subtitle: 'Illuminate Platform Training — Behavior Technician',
		bodyText:
			'has successfully completed all eight modules of Illuminate BT Training and passed the certification quiz, demonstrating proficiency in session creation, data collection, behavior recording, SOAP note documentation, and Meet & Greet notes.',
		contactEmail: 'onboarding@thelighthousech.com',
	},
	modules: [
		{
			id: 1,
			tag: 'Module 1 of 8',
			title: 'Logging in & your Dashboard',
			sections: [
				{ type: 'heading', text: 'Create your password' },
				{
					type: 'list',
					ordered: false,
					items: [
						"Open the <strong>Illuminate Password Verification link</strong> sent to your email and create a password. (If you can't find it, check your spam folder.)",
						'Log in to the Illuminate website with the password you created.',
					],
				},
				{ type: 'heading', text: 'The Dashboard' },
				{
					type: 'paragraph',
					text: "After logging in you'll land on the <strong>Dashboard</strong>. For BTs this is currently a simple welcome page — your real work happens in the <strong>Calendar</strong> tab.",
				},
				{
					type: 'callout',
					text: "<strong>Stuck at login?</strong> Request a password reset or contact the support team immediately — don't wait until your session starts.",
				},
			],
		},
		{
			id: 2,
			tag: 'Module 2 of 8',
			title: 'Calendar: starting a session',
			sections: [
				{
					type: 'paragraph',
					text: 'The <strong>Calendar tab</strong> is where you start sessions, take data, and write your SOAP notes.',
				},
				{ type: 'heading', text: 'Two ways to create a session' },
				{
					type: 'list',
					ordered: false,
					items: [
						'<strong>Schedule ahead:</strong> click the <strong>"+"</strong> icon to create a calendar event in advance.',
						'<strong>Start now:</strong> click <strong>"Start Session"</strong> when you\'re with your client in real time.',
					],
				},
				{ type: 'heading', text: 'Starting an instant session' },
				{
					type: 'list',
					ordered: true,
					items: [
						'Click <strong>Start Session</strong> (top right).',
						'Select your <strong>client</strong> from the dropdown.',
						'The session type will be <strong>97153</strong> — the behavior treatment code for BTs.',
						'The start time auto-fills with the current time; the end time defaults to one hour later. <strong>Edit the end time</strong> if your session is longer.',
						'Click <strong>Start Session</strong> to begin.',
					],
				},
				{
					type: 'callout',
					text: '<strong>Heads up:</strong> completed sessions cannot be deleted, so double-check the client and times before you start.',
				},
			],
		},
		{
			id: 3,
			tag: 'Module 3 of 8',
			title: 'Taking data on goals',
			sections: [
				{
					type: 'paragraph',
					text: "Once your session starts, you'll see the data-taking screen with the goals your BCBA has opened for your client. Click <strong>Target List</strong> to scroll through all of them.",
				},
				{ type: 'heading', text: 'The I and the P' },
				{
					type: 'paragraph',
					text: "<strong>I</strong> = Independent — the client completed the goal on their own. <strong>P</strong> = Prompt — the client needed prompting or couldn't complete it.",
				},
				{
					type: 'paragraph',
					text: 'Select a goal from the dropdown, click the <strong>+</strong> sign, then tap <strong>I</strong> or <strong>P</strong> for each trial. <strong>Data only counts if you enter the prompt codes (I and P).</strong>',
				},
				{ type: 'heading', text: 'The 3-goals-per-hour rule' },
				{
					type: 'paragraph',
					text: 'You must take data on at least <strong>3 separate goals for every hour</strong> of the session. A 1-hour session needs 3 goals; an 8-hour session needs 24.',
				},
				{ type: 'heading', text: 'Fixing mistakes' },
				{
					type: 'list',
					ordered: false,
					items: [
						'Tapped I instead of P (or vice versa)? Click the <strong>three lines / History</strong> next to the goal and adjust it.',
						'Delete a mistaken trial with the <strong>discard</strong> button.',
						'Use <strong>Hide</strong> to declutter long sessions — find hidden targets under <strong>Hidden Targets</strong> (top right).',
					],
				},
				{
					type: 'callout',
					text: '<strong>Questions about a goal?</strong> Each target shows its instructions — and your BCBA is your go-to for what they want on any specific goal.',
				},
			],
		},
		{
			id: 4,
			tag: 'Module 4 of 8',
			title: 'Recording maladaptive behaviors',
			sections: [
				{
					type: 'paragraph',
					text: "At the bottom of the data screen you'll see your client's <strong>maladaptive behaviors</strong> (for example, tantrums or hitting). Some have an <strong>info icon</strong> with more detail. There are two recording types:",
				},
				{ type: 'heading', text: 'Frequency' },
				{
					type: 'list',
					ordered: false,
					items: [
						'<strong>Tap the behavior each time it occurs</strong> — the count goes up by one.',
						'Tapped by accident? Tap the white area where the number shows and <strong>manually decrease</strong> it.',
					],
				},
				{ type: 'heading', text: 'Duration' },
				{
					type: 'list',
					ordered: false,
					items: [
						'<strong>Tap once to start the timer</strong> when the behavior begins.',
						'<strong>Tap again to stop it</strong> when the behavior ends.',
					],
				},
			],
		},
		{
			id: 5,
			tag: 'Module 5 of 8',
			title: 'SOAP notes & when to submit',
			sections: [
				{
					type: 'paragraph',
					text: "After you've recorded your data, click <strong>Start SOAP Note</strong>. This is the second part of every session note — a guided form with text fields and dropdowns covering location, participants, goals, client performance, and maladaptive behaviors. Your collected data populates onto the note.",
				},
				{ type: 'heading', text: 'Draft vs. Submit' },
				{
					type: 'list',
					ordered: false,
					items: [
						'You can <strong>save a draft</strong> and finish later.',
						'To <strong>submit</strong>, all required fields must be filled in.',
						'Find all your notes in the <strong>SOAP Notes tab</strong> — submitted notes show an <strong>eye icon</strong> to view the PDF.',
					],
				},
				{ type: 'heading', text: 'When to submit' },
				{
					type: 'callout',
					text: "<strong>Submit your session note within the last 10–15 minutes of the session</strong> — while you're still with the client and everything is fresh. Don't leave it for later: complete your data and SOAP note before the session ends.",
				},
			],
		},
		{
			id: 6,
			tag: 'Module 6 of 8',
			title: 'Meet & Greet: creating the session',
			sections: [
				{
					type: 'paragraph',
					text: "A <strong>Meet & Greet</strong> is an introductory session — the first time you and the client's family get to know each other before treatment begins. It requires its own note type in Illuminate, separate from your regular session notes.",
				},
				{ type: 'heading', text: 'Two ways to create the session' },
				{
					type: 'list',
					ordered: true,
					items: [
						'<strong>From the calendar:</strong> click the <strong>"+"</strong> on the day of the meet & greet to open the Add Session modal.',
						'<strong>Start now:</strong> click <strong>Start Session</strong> (top right of the Calendar tab).',
					],
				},
				{ type: 'heading', text: 'Setting up the session' },
				{ type: 'paragraph', text: 'In the Add Session modal:' },
				{
					type: 'list',
					ordered: true,
					items: [
						'Select your <strong>client</strong> from the dropdown.',
						'Under <strong>Session Type</strong>, choose <strong>"Meet and Greet"</strong> — not 97153.',
						'Set the correct <strong>start and end times</strong>.',
						'Click <strong>Start Session</strong>.',
					],
				},
				{
					type: 'callout',
					text: '<strong>Important:</strong> The session type <em>must</em> be "Meet and Greet." Using 97153 by mistake creates a billable treatment session instead.',
				},
			],
		},
		{
			id: 7,
			tag: 'Module 7 of 8',
			title: 'Meet & Greet: filling out the note',
			sections: [
				{
					type: 'paragraph',
					text: 'Once the session is created, open it and click <strong>Start SOAP Note</strong> (or the note icon) to open the Meet & Greet note form.',
				},
				{
					type: 'callout',
					text: '<strong>No data entry required.</strong> There are no goals or behaviors to record — this note is narrative only.',
				},
				{ type: 'heading', text: 'Fields to complete' },
				{
					type: 'list',
					ordered: false,
					items: [
						'<strong>Start time / End time / Duration</strong> — auto-filled; adjust if needed.',
						'<strong>Behavior Technician</strong> — your name, pre-filled.',
						'<strong>Location</strong> — where the meet & greet took place (Home, Office, Community, etc.).',
						'<strong>Session Participants</strong> — everyone present: Client, Behavior Tech, Parent(s)/Legal Guardian(s), and any others.',
						'<strong>Barriers to Treatment</strong> — any barriers discussed (e.g., travel distance, scheduling). Select all that apply.',
						'<strong>Session Summary</strong> — a brief narrative: introductions, treatment plan discussion, caregiver questions, client observations, and next steps.',
						'<strong>Printed Name & Signature</strong> — sign off as you would on any note.',
					],
				},
				{ type: 'heading', text: 'Submitting' },
				{
					type: 'paragraph',
					text: 'Review all fields, then click <strong>Submit</strong>. You can <strong>Save as Draft</strong> if needed — but aim to complete and submit before or shortly after the meet & greet ends.',
				},
			],
		},
		{
			id: 8,
			tag: 'Module 8 of 8',
			title: 'Getting help',
			sections: [
				{
					type: 'paragraph',
					text: 'Issues with Illuminate can affect client services <em>and</em> your payroll — so never sit on a problem.',
				},
				{
					type: 'list',
					ordered: false,
					items: [
						'Use the <strong>support button</strong> in Illuminate to submit a ticket for technical or training issues.',
						'Reach out to the support team <strong>as soon as</strong> something looks wrong with a session or note.',
						'For questions about a specific goal or what data your BCBA wants — <strong>ask your BCBA</strong>.',
					],
				},
				{ type: 'heading', text: 'Quick recap' },
				{
					type: 'list',
					ordered: false,
					items: [
						'Calendar tab → Start Session → take data (I / P) → behaviors → SOAP note.',
						'Meet & Greet notes: select "Meet and Greet" session type — no data entry required.',
						'3 goals per hour, every hour (regular sessions).',
						'Submit your session note within the last 10–15 minutes of the session.',
						"Completed sessions can't be deleted.",
					],
				},
			],
		},
	],
	quiz: [
		{
			id: 1,
			question: 'On the data-taking screen, what do the I and P buttons stand for?',
			options: ['Initial and Pending', 'Independent and Prompt', 'Input and Pause', 'Important and Priority'],
			correctIndex: 1,
			explanation: 'I = the client completed the goal independently; P = the client needed prompting.',
		},
		{
			id: 2,
			question: 'How many separate goals must you take data on for every hour of a session?',
			options: ['1', '2', '3', '5'],
			correctIndex: 2,
			explanation: 'The requirement is 3 goals per hour — so an 8-hour session needs 24 goals.',
		},
		{
			id: 3,
			question: 'Your session is 4 hours long. How many goals do you need to take data on?',
			options: ['3', '4', '8', '12'],
			correctIndex: 3,
			explanation: '3 goals × 4 hours = 12 separate goals.',
		},
		{
			id: 4,
			question: 'When should you submit your session note?',
			options: [
				'Within 48 hours of the session',
				'Within the last 10–15 minutes of the session',
				'By the end of the week',
				'Anytime before payroll runs',
			],
			correctIndex: 1,
			explanation:
				'Session notes must be submitted within the last 10–15 minutes of the session, before it ends — not afterward.',
		},
		{
			id: 5,
			question: 'Which session type (CPT code) do BTs use for their sessions?',
			options: ['97151', '97153', '97155', '90837'],
			correctIndex: 1,
			explanation: '97153 is the behavior treatment code for BT sessions.',
		},
		{
			id: 6,
			question: 'How do you record a duration-type maladaptive behavior?',
			options: [
				'Tap it once for each occurrence',
				'Type the number of minutes manually',
				'Tap to start the timer, tap again to stop it',
				'Write it in the SOAP note only',
			],
			correctIndex: 2,
			explanation: 'Duration behaviors run on a timer: tap to start when the behavior begins, tap again when it ends.',
		},
		{
			id: 7,
			question: 'Which tab do you use to start a session and take your session notes?',
			options: ['Dashboard', 'Clients', 'Calendar', 'SOAP Notes'],
			correctIndex: 2,
			explanation: 'The Calendar tab is where you start sessions, take data, and write SOAP notes.',
		},
		{
			id: 8,
			question: 'Why is your Illuminate documentation so important? (Pick the best answer)',
			options: [
				"It tracks the client's progress, supports their treatment, and tracks your hours for payroll",
				'It is only used for insurance audits',
				'It replaces talking to your BCBA',
				'It is optional unless your BCBA asks',
			],
			correctIndex: 0,
			explanation:
				"Accurate data drives the client's progress tracking, helps the BCBA make the right treatment decisions, and tracks your hours for payroll.",
		},
		{
			id: 9,
			question: "You accidentally tapped a frequency behavior that didn't occur. What do you do?",
			options: [
				"Nothing — counts can't be changed",
				'Delete the whole session and restart',
				'Tap the number area and manually decrease the count',
				'Submit a support ticket',
			],
			correctIndex: 2,
			explanation: 'Tap the white area where the number shows and manually decrease it.',
		},
		{
			id: 10,
			question: 'What happens to a session once it is completed?',
			options: [
				'It can be deleted from the calendar anytime',
				'It cannot be deleted',
				'It auto-deletes after 48 hours',
				'Only your BCBA can delete it',
			],
			correctIndex: 1,
			explanation: 'Completed sessions cannot be deleted — double-check details before starting.',
		},
		{
			id: 11,
			question: 'Which session type should you select in Illuminate when creating a Meet & Greet session?',
			options: ['97153', '97151', 'Meet and Greet', '90837'],
			correctIndex: 2,
			explanation:
				'Meet & Greet sessions require the "Meet and Greet" session type — using 97153 would create a billable treatment session instead.',
		},
		{
			id: 12,
			question: 'Do you need to record goal data (I/P) during a Meet & Greet session?',
			options: [
				'Yes, at least 3 goals per hour',
				'Yes, but only 1 goal',
				'No — Meet & Greet notes are narrative only',
				'Only if the BCBA asks',
			],
			correctIndex: 2,
			explanation:
				'Meet & Greet sessions do not have goals or maladaptive behaviors to record. The note is narrative only — focus on the Session Summary.',
		},
	],
};
